import { Box, TextField, Icon, Popover, DatePicker } from "@shopify/polaris";
import type { Range } from "@shopify/polaris";
import React, { useState, useCallback } from "react";
import { CalendarIcon } from "@shopify/polaris-icons";
import { formatDateStringToMMDYY, MONTHS } from "./utils";

const getCurrentMonthAndYear = (
  date: Date,
): {
  month: number;
  year: number;
} => {
  const dateParts = date.toDateString().split(" ").slice(1);
  return { month: Number(MONTHS[dateParts[0]]) - 1, year: Number(dateParts[2]) };
};

function CustomDatePicker({
  id,
  label,
  helpText,
  placeholder,
  date,
  setDate,
  disabled = false,
  firstSelectableDate,
  onClear,
}: {
  readonly id?: string;
  readonly label: string | React.ReactNode;
  readonly helpText?: string;
  readonly placeholder: string;
  readonly date: Date | undefined;
  readonly setDate: (date: Date) => void;
  readonly disabled?: boolean;
  readonly firstSelectableDate?: Date;
  readonly onClear?: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  // eslint-disable-next-line react/hook-use-state
  const [{ month, year }, setPickerDate] = useState(getCurrentMonthAndYear(date ?? new Date()));

  const handlePickerChange = useCallback((newMonth: number, newYear: number) => {
    setPickerDate({ month: newMonth, year: newYear });
  }, []);

  const handleDateChange = useCallback(
    ({ start }: Range): void => {
      setOpen(false);
      setDate(start);
    },
    [setDate, setOpen],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const displayDate = date ? formatDateStringToMMDYY(date) : "";

  return (
    <Box data-polaris-layer>
      <Popover
        activator={
          <TextField
            autoComplete="off"
            clearButton={Boolean(onClear)}
            disabled={disabled}
            helpText={helpText}
            id={id}
            label={label}
            onClearButtonClick={onClear}
            onFocus={handleOpen}
            placeholder={placeholder}
            prefix={<Icon source={CalendarIcon} tone="base" />}
            role="combobox"
            type="text"
            value={displayDate}
          />
        }
        active={open}
        autofocusTarget="none"
        fluidContent={false}
        onClose={handleClose}
        preferInputActivator={false}
        zIndexOverride={1400}
      >
        {!disabled && (
          <Popover.Pane>
            <Popover.Section>
              <DatePicker
                disableDatesBefore={firstSelectableDate}
                month={month}
                onChange={handleDateChange}
                onMonthChange={handlePickerChange}
                selected={date}
                year={year}
              />
            </Popover.Section>
          </Popover.Pane>
        )}
      </Popover>
    </Box>
  );
}

export default CustomDatePicker;
